/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MultipleDatePicker } from '@modernloop/shared/components';
import { IsoDateFormat, getLocalTimezone } from '@modernloop/shared/datetime';
import { Box, BoxProps, Typography } from '@mui/material';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import {
  addDays,
  addHours,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isPast,
  startOfDay as startOfDayDateFns,
  startOfToday,
} from 'date-fns';
import { difference } from 'lodash';

import {
  DateTimeRangeOutput,
  JobStageInterviewGroupInput,
  SelfScheduleZoomHost,
  SuggestedTimeRangesRequestType,
} from 'src/generated/mloop-graphql';

import CompanyHolidayIcon from 'src/entities/CompanyHolidayIcon';
import useGetCompanyHolidaysForDate, {
  CompanyHolidaysForDays,
} from 'src/entities/CompanyHolidayIcon/useGetCompanyHolidaysForDate';

import usePrevious from 'src/hooks/usePrevious';

import IsoTimestamp, { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import { endOfDay } from 'src/utils/dateUtils';

import OptionDay from './OptionDay';
import useLoadSelfScheduleOptionsByTimeRange, { TimeRangesByDayMap } from './useLoadSelfScheduleOptionsByTimeRange';

type Props = {
  applicationId: string;
  taskId?: string;
  requestType: SuggestedTimeRangesRequestType;
  jobStageId: string;
  customInterviewPlan?: JobStageInterviewGroupInput[];
  customJobStageId: string | undefined; // This can be either original or custom job stage id
  rollingDays?: number;
  inclusionDays?: IsoTimestamp[];
  selfScheduleZoomHost?: SelfScheduleZoomHost;
  advanceNoticeInHours?: number;
  onLoading: (loading: boolean) => void;
  onSelectedDatesOptionsCountChanged: (newCount: number) => void;
  onSelectedDatesChanged: (dates: IsoTimestamp[]) => void;
  onEmployeeMissingZoomUserId: (employeeIds: string[]) => void;
  onSelfScheduleCountError: (errorMessage: string) => void;
  shouldRespectLoadLimit: boolean;
  canScheduleOverAvailableKeywords: boolean;
  canScheduleOverRecruitingKeywords: boolean;
  canScheduleOverFreeTime: boolean;
  skipFetchingOptions?: boolean;
};

const HolidayIconWrapperProps: BoxProps = {
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  zIndex: 9,
};

const OptionsCalendar = ({
  applicationId,
  taskId,
  requestType,
  jobStageId,
  customJobStageId,
  customInterviewPlan,
  rollingDays,
  inclusionDays,
  selfScheduleZoomHost,
  advanceNoticeInHours,
  onLoading,
  onSelectedDatesOptionsCountChanged,
  onSelectedDatesChanged,
  onEmployeeMissingZoomUserId,
  onSelfScheduleCountError,
  shouldRespectLoadLimit,
  canScheduleOverAvailableKeywords,
  canScheduleOverRecruitingKeywords,
  canScheduleOverFreeTime,
  skipFetchingOptions,
}: Props) => {
  const timezone = getLocalTimezone();
  const [selectedDates, setSelectedDates] = useState<IsoTimestamp[]>([]);
  const [countByDayMap, setCountByDayMap] = useState<{ [day: IsoTimestamp]: number }>({});
  const [timeRangesByDayMap, setTimeRangesByDayMap] = useState<TimeRangesByDayMap>({});
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadPendingDays, setLoadPendingDays] = useState(false);

  const timeRangesLoadedRef = useRef<DateTimeRangeOutput[]>([]);

  const prevSelfScheduleZoomHost = usePrevious(selfScheduleZoomHost);
  const prevAdvanceNoticeInHours = usePrevious(advanceNoticeInHours);
  const shouldSkipFetchingOptions = skipFetchingOptions;

  const loadSelfScheduleOptionsByTimeRange = useLoadSelfScheduleOptionsByTimeRange({
    applicationId,
    onEmployeeMissingZoomUserId,
    onError: onSelfScheduleCountError,
    requestType,
    selfScheduleZoomHost,
    taskId,
  });

  /** Start and end date for calendar option range */
  const startDateRangeIsoTime: IsoTimestamp = assertIsoTimestamp(
    startOfDayDateFns(addHours(new Date(), advanceNoticeInHours ?? 0)).toISOString()
  );
  const endDateRangeIsoTime: IsoTimestamp = assertIsoTimestamp(endOfMonth(addMonths(new Date(), 2)).toISOString());

  const dateToCompanyHolidays: CompanyHolidaysForDays = useGetCompanyHolidaysForDate(
    startDateRangeIsoTime,
    endDateRangeIsoTime
  );

  const advanceNoticeDays = useMemo(() => {
    const result: IsoTimestamp[] = [];
    if (!advanceNoticeInHours) {
      return result;
    }

    let date = startOfDayDateFns(new Date());

    for (let i = 0; i < advanceNoticeInHours / 24; i++) {
      result.push(assertIsoTimestamp(date.toISOString()));
      date = addDays(date, 1);
    }

    return result;
  }, [advanceNoticeInHours]);

  const updateTimeRangesLoaded = useCallback((timeRanges: DateTimeRangeOutput[]) => {
    timeRanges.forEach((range) => {
      const index = timeRangesLoadedRef.current.findIndex(
        (dateRange) => dateRange.startAt === range.startAt && dateRange.endAt === range.endAt
      );

      if (index !== -1) {
        return;
      }
      timeRangesLoadedRef.current.push(range);
    });
  }, []);

  // This calculates the new selected dates when `rollingNumberOfWeeks` changes.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    setLoadPendingDays(true);
    if (inclusionDays) {
      setSelectedDates(inclusionDays);
      return;
    }

    if (!rollingDays) {
      return;
    }
    let date = startOfDayDateFns(new Date());
    const dates: IsoTimestamp[] = [];

    for (let i = 0; i < rollingDays; i++) {
      if (!advanceNoticeDays.includes(assertIsoTimestamp(date.toISOString()))) {
        dates.push(assertIsoTimestamp(date.toISOString()));
      }
      date = addDays(date, 1);
    }

    setSelectedDates(dates);
  }, [advanceNoticeDays, inclusionDays, rollingDays]);

  // Reset countByDayMap when Zoom host changes
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (prevSelfScheduleZoomHost === selfScheduleZoomHost) {
      return;
    }
    setCountByDayMap({});
    setTimeRangesByDayMap({});
  }, [prevSelfScheduleZoomHost, selfScheduleZoomHost]);

  // Calculates if there is a change in number of selected dates and updates the candidate options count.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    let count = 0;

    Object.keys(countByDayMap).forEach((key: IsoTimestamp) => {
      if (!selectedDates.includes(key) || advanceNoticeDays.includes(key)) {
        return;
      }
      count += countByDayMap[key];
    });

    onSelectedDatesOptionsCountChanged(count);
  }, [advanceNoticeDays, countByDayMap, onSelectedDatesOptionsCountChanged, selectedDates]);

  const handleSelectedDatesChanged = (dates: IsoTimestamp[]) => {
    setSelectedDates(dates);
    if (onSelectedDatesChanged) {
      onSelectedDatesChanged(dates);
    }
  };

  const getTimeRangeByWeek = useCallback((firstDay: Date, lastDay: Date): DateTimeRangeOutput[] => {
    const timeRanges: DateTimeRangeOutput[] = [];
    let temp = firstDay;

    while (temp <= lastDay) {
      const endAt = endOfWeek(temp);

      timeRanges.push({
        startAt: temp.toISOString(),
        endAt: endAt.toISOString(),
        start: undefined,
        end: undefined,
      });

      // Set temp to the day after the end of the week
      temp = startOfDayDateFns(addDays(endAt, 1));
    }

    return timeRanges;
  }, []);

  const handleMonthChange = useCallback(
    (newMonth: IsoTimestamp) => {
      const firstDay = isPast(new Date(newMonth)) ? startOfToday() : new Date(newMonth);
      const lastDay = endOfMonth(firstDay);
      const timeRanges = getTimeRangeByWeek(firstDay, lastDay);

      onLoading(true);
      setLoadingOptions(true);

      updateTimeRangesLoaded(timeRanges);
      // eslint-disable-next-line promise/catch-or-return
      loadSelfScheduleOptionsByTimeRange(
        timeRanges,
        timezone,
        advanceNoticeInHours,
        countByDayMap,
        setCountByDayMap,
        timeRangesByDayMap,
        setTimeRangesByDayMap,
        jobStageId,
        shouldRespectLoadLimit,
        canScheduleOverAvailableKeywords,
        canScheduleOverRecruitingKeywords,
        canScheduleOverFreeTime,
        customJobStageId,
        'network-only',
        customInterviewPlan,
        shouldSkipFetchingOptions
      ).finally(() => {
        onLoading(false);
        setLoadingOptions(false);
      });
    },
    [
      getTimeRangeByWeek,
      onLoading,
      updateTimeRangesLoaded,
      loadSelfScheduleOptionsByTimeRange,
      timezone,
      advanceNoticeInHours,
      countByDayMap,
      timeRangesByDayMap,
      jobStageId,
      shouldRespectLoadLimit,
      canScheduleOverAvailableKeywords,
      canScheduleOverRecruitingKeywords,
      canScheduleOverFreeTime,
      customJobStageId,
      customInterviewPlan,
      shouldSkipFetchingOptions,
    ]
  );

  // Fetch options count when the component mounts
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    handleMonthChange(assertIsoTimestamp(startOfDayDateFns(new Date()).toISOString()));
    // disabling dependency check because we want to fire this effect only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch options count for days that are part of the selection but are not yet fetched.
  // This function loads in time ranges of a week from Sun → Sat
  // E.g. When use current date is 20 Jan and user has selected 3 weeks
  //      in this case in addition to loading options count for month of Jan we also need to fetch
  //      count for Feb until 9th.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect runs imperative data work when dependencies change; prefer a data hook when possible.
  useEffect(() => {
    if (loadingOptions || !loadPendingDays) {
      return;
    }

    setLoadPendingDays(false);

    const datesToLoad = difference(selectedDates, Object.keys(countByDayMap));
    datesToLoad.sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    if (!datesToLoad || !datesToLoad.length) {
      return;
    }

    const timeRanges = getTimeRangeByWeek(new Date(datesToLoad[0]), new Date(datesToLoad[datesToLoad.length - 1]));

    onLoading(true);
    setLoadingOptions(true);

    updateTimeRangesLoaded(timeRanges);
    // eslint-disable-next-line promise/catch-or-return
    loadSelfScheduleOptionsByTimeRange(
      timeRanges,
      timezone,
      advanceNoticeInHours,
      countByDayMap,
      setCountByDayMap,
      timeRangesByDayMap,
      setTimeRangesByDayMap,
      jobStageId,
      shouldRespectLoadLimit,
      canScheduleOverAvailableKeywords,
      canScheduleOverRecruitingKeywords,
      canScheduleOverFreeTime,
      customJobStageId,
      'network-only',
      customInterviewPlan,
      shouldSkipFetchingOptions
    ).finally(() => {
      onLoading(false);
      setLoadingOptions(false);
    });
  }, [
    countByDayMap,
    timeRangesByDayMap,
    customJobStageId,
    customInterviewPlan,
    getTimeRangeByWeek,
    jobStageId,
    shouldRespectLoadLimit,
    canScheduleOverAvailableKeywords,
    canScheduleOverRecruitingKeywords,
    canScheduleOverFreeTime,
    loadingOptions,
    loadSelfScheduleOptionsByTimeRange,
    onLoading,
    selectedDates,
    timezone,
    updateTimeRangesLoaded,
    loadPendingDays,
    advanceNoticeInHours,
    shouldSkipFetchingOptions,
  ]);

  /**
   * Used to refetch options count for all the date ranges fetched so far
   * when there is any change to SelfScheduleZoomHost
   */
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect runs imperative data work when dependencies change; prefer a data hook when possible.
  useEffect(() => {
    if (prevSelfScheduleZoomHost === selfScheduleZoomHost) {
      return;
    }

    onLoading(true);
    setLoadingOptions(true);

    // eslint-disable-next-line promise/catch-or-return
    loadSelfScheduleOptionsByTimeRange(
      timeRangesLoadedRef.current,
      timezone,
      advanceNoticeInHours,
      {},
      setCountByDayMap,
      {},
      setTimeRangesByDayMap,
      jobStageId,
      shouldRespectLoadLimit,
      canScheduleOverAvailableKeywords,
      canScheduleOverRecruitingKeywords,
      canScheduleOverFreeTime,
      customJobStageId,
      'network-only',
      customInterviewPlan,
      shouldSkipFetchingOptions
    ).finally(() => {
      onLoading(false);
      setLoadingOptions(false);
    });
  }, [
    customJobStageId,
    customInterviewPlan,
    getTimeRangeByWeek,
    jobStageId,
    shouldRespectLoadLimit,
    canScheduleOverAvailableKeywords,
    canScheduleOverRecruitingKeywords,
    canScheduleOverFreeTime,
    loadSelfScheduleOptionsByTimeRange,
    onLoading,
    prevSelfScheduleZoomHost,
    selectedDates,
    selfScheduleZoomHost,
    timezone,
    advanceNoticeInHours,
    shouldSkipFetchingOptions,
  ]);

  /**
   * Used to refetch options count for all the date ranges fetched so far
   * when there is any change to advanceNoticeInHours
   */
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect runs imperative data work when dependencies change; prefer a data hook when possible.
  useEffect(() => {
    if (
      prevAdvanceNoticeInHours === null ||
      prevAdvanceNoticeInHours === undefined ||
      prevAdvanceNoticeInHours !== advanceNoticeInHours
    ) {
      return;
    }

    onLoading(true);
    setLoadingOptions(true);

    // eslint-disable-next-line promise/catch-or-return
    loadSelfScheduleOptionsByTimeRange(
      timeRangesLoadedRef.current,
      timezone,
      advanceNoticeInHours,
      {},
      setCountByDayMap,
      {},
      setTimeRangesByDayMap,
      jobStageId,
      shouldRespectLoadLimit,
      canScheduleOverAvailableKeywords,
      canScheduleOverRecruitingKeywords,
      canScheduleOverFreeTime,
      customJobStageId,
      'network-only',
      customInterviewPlan,
      shouldSkipFetchingOptions
    ).finally(() => {
      onLoading(false);
      setLoadingOptions(false);
    });
  }, [
    advanceNoticeInHours,
    customInterviewPlan,
    customJobStageId,
    shouldRespectLoadLimit,
    canScheduleOverAvailableKeywords,
    canScheduleOverRecruitingKeywords,
    canScheduleOverFreeTime,
    jobStageId,
    loadSelfScheduleOptionsByTimeRange,
    onLoading,
    prevAdvanceNoticeInHours,
    timezone,
    shouldSkipFetchingOptions,
  ]);

  const renderDay = (
    props: Parameters<NonNullable<React.ComponentProps<typeof MultipleDatePicker>['renderDay']>>[0]
  ) => {
    const { day } = props;
    const companyHolidays = dateToCompanyHolidays[format(day, IsoDateFormat)] || null;

    const isPastDay = isPast(endOfDay(day, timezone));
    const isPartOfAdvanceNoticeDays = advanceNoticeDays.includes(assertIsoTimestamp(day));

    if (day && (isPastDay || isPartOfAdvanceNoticeDays)) {
      return (
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          borderRadius="6px"
          width="44px"
          height="44px"
          flexGrow={1}
        >
          <Typography variant="caption" fontWeight={500} sx={{ textAlign: 'center' }} color="text.secondary">
            {new Date(day).getDate()}
          </Typography>

          <CompanyHolidayIcon companyHolidays={companyHolidays} iconWrapperProps={HolidayIconWrapperProps} />
        </Box>
      );
    }

    const dayIsoTimeStamp = day;
    const options = countByDayMap[dayIsoTimeStamp];

    return (
      <Box position="relative" flexGrow={1} bgcolor="background.default">
        <OptionDay
          options={options}
          timeRanges={timeRangesByDayMap[dayIsoTimeStamp]}
          pickerDayProps={props as unknown as PickersDayProps<IsoTimestamp>}
          selected={Boolean(day && selectedDates.includes(assertIsoTimestamp(day)))}
          timezone={timezone}
        />

        <CompanyHolidayIcon companyHolidays={companyHolidays} iconWrapperProps={HolidayIconWrapperProps} />
      </Box>
    );
  };

  return (
    <Box width="360px" height="400px">
      <MultipleDatePicker
        minDate={startDateRangeIsoTime}
        maxDate={endDateRangeIsoTime}
        selectedDates={selectedDates}
        timezone={timezone}
        onSelectedDatesChanged={handleSelectedDatesChanged}
        renderDay={renderDay}
        onMonthChange={handleMonthChange}
        shouldDisableDate={(day: IsoTimestamp) => advanceNoticeDays.includes(day)}
      />
    </Box>
  );
};

export default OptionsCalendar;
